import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import Typography from "../../../Typography";
import CompanyData from "../../../variables/CompanyData";
import Button from "../../components/Button";

// useInView Hook
const useInView = (options = { threshold: 0.1 }) => {
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.unobserve(entry.target); // Stop observing once in view
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options]);

  return [ref, inView];
};

// AnimatedNumber Component
const AnimatedNumber = ({ targetNumber }: { targetNumber: number }) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView) {
      const duration = 2; // Duration in seconds
      const frames = duration * 60; // Assuming 60 frames per second
      const step = targetNumber / frames;

      let currentCount = 0;

      const updateCount = () => {
        currentCount += step;
        if (currentCount >= targetNumber) {
          setCount(targetNumber); // Set the count to target when it reaches or exceeds
        } else {
          setCount(currentCount);
          requestAnimationFrame(updateCount);
        }
      };

      updateCount();

      // Animate with framer-motion
      controls.start({
        opacity: 1,
        transition: { duration: 1, ease: "easeOut" },
      });
    }
  }, [inView, targetNumber, controls]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0 }}
      animate={controls}
      className="text-white"
    >
      {Math.round(count)} {/* Round to avoid decimals */}
    </motion.span>
  );
};

// AboutUs Component
export default function AboutUs() {
  const Highlights = [
    {
      icon: "./imgs/icons/consulattions.svg",
      no: 30000,
      simbol: "+",
      name: "Consultations Provided",
    },
    {
      icon: "./imgs/icons/satisfied.svg",
      no: 95,
      simbol: "%",
      name: "Satisfied Clients",
    },
    {
      icon: "./imgs/icons/finished.svg",
      no: 10000,
      simbol: "+",
      name: "Finished Tattoos",
    },
    {
      icon: "./imgs/icons/experience.svg",
      no: 20,
      simbol: "+",
      name: "Years of Experience",
    },
    {
      icon: "./imgs/icons/repeat.svg",
      no: "70",
      simbol: "%",
      name: "Repeat Clients",
    },
  ];

  return (
    <div className="flex bg-black flex-col">
      <div className="max-md:gap-[0] flex w-full gap-[35px] items-center max-md:flex-col">
        <div className="max-md:min-h-[300px] max-md:w-full min-w-[40%] max-lg:min-h-[500px] max-xl:min-h-[600px] w-[50%] relative h-full min-h-[670px]">
          <img
            alt="From studio"
            className="w-full object-cover h-[100%] absolute top-0 bottom-0 left-0 right-0"
            src="./imgs/about-us.jpg"
          ></img>
        </div>

        <div className="max-w-[650px] py-[50px] px-[15px]">
          <Typography.Subt className="text-white">
            Why we are different
          </Typography.Subt>
          <Typography.P className="text-white font-[600] mt-[10px]">
            Our works speak about the quality of our tattoo artists.
          </Typography.P>
          <Typography.P className="text-white mt-[20px] opacity-80">
            What definitely sets us apart from the competition is the fact that
            we always guarantee the quality of the tattoo, as well as a
            completely unique tattoo design. Your tattoo is a part of you and
            it's always up to you, that's why we approach every project with the
            utmost seriousness and understand our work first and foremost as
            art. {CompanyData[0].name} Tattoo studio covers all styles of
            tattooing - from the simplest to the extremely complex. We also do
            what very few artists can do and few dare to do, which is cover-up
            tattoos with great success, to the great satisfaction of our
            clients. We are best known for realistic tattoo painting projects,
            dotwork style, color tattoos, color realism, as well as the abstract
            style of some of our tattoo artists. Because of all this, we
            cooperate with a large number of clients who do not stop at just one
            tattoo.
          </Typography.P>
          <Button variant="secondary" className="mt-[30px]">
            Book Now
          </Button>
        </div>
      </div>
      <div className="max-xl:flex-wrap flex gap-[10px] mt-[10px]">
        {Highlights.map((highlight, index) => (
          <motion.div
            key={index}
            className={`max-xsm:w-[100%] min-h-[180px] bg-white w-full bg-opacity-10 flex items-center justify-center gap-[8px] flex-col ${
              index === 0 ? "max-xl:w-full" : "max-xl:w-[calc(50%-5px)]"
            }`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="max-w-[40px] h-[45px] flex items-center justify-center">
              <img src={highlight.icon} className="w-full" alt="Icon"></img>
            </div>
            <Typography.BoxName className="text-white flex">
              <div className="flex gap-[1px]">
                <AnimatedNumber targetNumber={highlight.no} />
                {highlight.simbol}
              </div>
            </Typography.BoxName>
            <Typography.P className="text-white opacity-80">
              {highlight.name}
            </Typography.P>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
